import { type ForwardedRef, forwardRef, lazy, memo, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_FEATURE_EVENT } from '../../featureEvents';
import { withTraceId } from '../../lifecycle/tracker.helpers';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import MessageCopyButton from '../MessageCopyButton';
import MessageFeedbackRow from '../MessageFeedbackRow';
import MessageShareButton, { getShareType } from '../MessageShareButton';
import MessageActionResult from './MessageActionResult';
import useStyles from './MessageBubble.styles';
import MessageCitations from './MessageCitations';
import MessageMarkdown from './MessageMarkdown';
import MessageVisualizationImages from './MessageVisualizationImages';
import type { MessageBubbleProps } from './types';
import UserMessageTurn from './UserMessageTurn';

export type { MessageBubbleProps } from './types';

/**
 * `MessageDataTable` is lazy-loaded so the `material-react-table`
 * dependency tree (and its transitive MRT/emotion chunks) only enters
 * the bundle when a message actually carries a table visualization.
 * The `showVisualization` guard means non-table turns never trigger
 * the import.
 */
const MessageDataTable = lazy(() => import('../MessageDataTable'));

const SQL_FENCE_PATTERN = /```sql[\s\S]*?```/gi;
const GENERATED_SQL_SECTION_PATTERN =
  /(^|\n)(?:#{1,6}\s*)?(?:\*\*)?(?:generated\s+)?sql(?:\s+query)?(?:\*\*)?:?\s*\n(?:select|with)\b[\s\S]*?(?=\n\n|$)/gi;

/** Removes backend SQL/debug snippets that are not user-facing answer content. */
export const stripAssistantSqlDebugContent = (content: string): string =>
  content
    .replace(SQL_FENCE_PATTERN, '')
    .replace(GENERATED_SQL_SECTION_PATTERN, '\n')
    .trim();

/**
 * One chat turn as a styled row and bubble.
 *
 * For non-user roles (`assistant`, `system`, `tool`) the bubble is
 * stacked vertically with a `MessageFeedbackRow` so the user can
 * react to each response. The feedback row is a sibling of the
 * bubble (not nested inside it) so the bubble's `aria-label` does
 * not envelop the buttons.
 */
function MessageBubbleInner(
  {
    message,
    isM1Enabled = false,
    userControls,
    findArtifact,
    className,
  }: MessageBubbleProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const isUser = message.role === 'user';
  const role = isUser ? 'user' : 'assistant';
  const ariaLabel = isUser
    ? t('chat.messages.userAriaLabel')
    : t('chat.messages.assistantAriaLabel');
  const showVisualization = !isUser && message.visualization?.type === 'table';
  const showVisualizationImages =
    !isUser &&
    message.visualizationUrls !== undefined &&
    message.visualizationUrls.length > 0;
  const assistantContent = isUser
    ? message.content
    : stripAssistantSqlDebugContent(message.content);
  // Copy applies to every turn, so it is deliberately not gated on
  // `traceId` the way the feedback row is.
  const copyButton = isM1Enabled ? (
    <MessageCopyButton
      text={assistantContent}
      ariaLabel={
        isUser
          ? t('chat.copy.queryAriaLabel')
          : t('chat.copy.responseAriaLabel')
      }
      testId={`ai-sidekick-copy-${message.id}`}
      copiedEvent={{
        type: SIDEKICK_FEATURE_EVENT.MESSAGE_COPIED,
        conversationId: message.conversationId,
        target: isUser ? 'query' : 'response',
        ...(isUser ? {} : withTraceId(message.traceId)),
      }}
    />
  ) : null;
  // The share email carries the stored text unchanged, so a response
  // whose SQL/debug content the bubble strips is not offered: the
  // recipients would read what the chat hides.
  const hasHiddenSqlDebugContent =
    !isUser && assistantContent !== message.content.trim();
  // Sharing sends a response on to someone else, so it is offered on
  // assistant turns only — never on the user's own question, and not
  // on the `system` and `tool` turns the timeline also renders as
  // assistant-style bubbles. An answer the chat composed itself has no
  // stored copy for the share endpoint to send.
  const shareButton =
    isM1Enabled &&
    message.role === 'assistant' &&
    message.isLocalOnly !== true &&
    !hasHiddenSqlDebugContent ? (
      <MessageShareButton
        conversationId={message.conversationId}
        messageId={message.id}
        content={message.content}
        createdAt={message.createdAt}
        shareType={getShareType({
          hasTable: showVisualization,
          hasChart: showVisualizationImages,
        })}
        artifacts={message.artifacts}
        traceId={message.traceId}
        testId={`ai-sidekick-share-${message.id}`}
      />
    ) : null;
  // Figma orders the response actions Copy, Share, then the thumbs,
  // which is what the feedback row's leading slot renders ahead of.
  const assistantLeadingActions =
    copyButton === null && shareButton === null ? null : (
      <>
        {copyButton}
        {shareButton}
      </>
    );
  // Feedback names the message it rates, and the backend never issued an
  // id for an answer the chat composed itself.
  const feedbackRow =
    message.role === 'assistant' &&
    message.isLocalOnly !== true &&
    message.traceId !== undefined &&
    message.traceId !== '' ? (
      <MessageFeedbackRow
        messageId={message.id}
        conversationId={message.conversationId}
        traceId={message.traceId}
        leadingActions={assistantLeadingActions}
      />
    ) : null;
  // Without a feedback row there is no button row to lead, so the
  // leading actions get a row of their own.
  const assistantActions =
    feedbackRow ??
    (assistantLeadingActions === null ? null : (
      <div className={classes.assistantActions}>{assistantLeadingActions}</div>
    ));

  const assistantBubble = (
    <article
      className={cx(classes.bubble, classes.bubbleAssistant)}
      aria-label={ariaLabel}
    >
      <MessageMarkdown
        content={assistantContent}
        citations={message.citations}
      />
    </article>
  );

  return (
    <div
      ref={ref}
      className={cx(
        classes.row,
        isUser ? classes.rowUser : classes.rowAssistant,
        className
      )}
      data-role={role}
      data-testid={`ai-sidekick-message-${message.id}`}
    >
      {isUser ? (
        <UserMessageTurn
          message={message}
          ariaLabel={ariaLabel}
          copyButton={copyButton}
          controls={isM1Enabled ? userControls : undefined}
        />
      ) : (
        <div className={classes.assistantStack}>
          {assistantBubble}
          {showVisualization && message.visualization !== undefined && (
            <div className={classes.visualizationSlot}>
              <Suspense fallback={null}>
                <MessageDataTable
                  visualization={message.visualization}
                  isM1Enabled={isM1Enabled}
                  resolveArtifact={
                    findArtifact === undefined
                      ? undefined
                      : () => findArtifact(message.id, 'table', 0)
                  }
                />
              </Suspense>
            </div>
          )}
          {showVisualizationImages && (
            <MessageVisualizationImages
              isM1Enabled={isM1Enabled}
              title={message.visualization?.title}
              visualizationUrls={message.visualizationUrls}
              resolveChartArtifact={
                findArtifact === undefined
                  ? undefined
                  : (chartIndex) =>
                      findArtifact(message.id, 'chart', chartIndex)
              }
            />
          )}
          {isM1Enabled &&
            message.actionResults?.map((actionResult) => (
              <MessageActionResult
                key={actionResult.id}
                actionResult={actionResult}
                answerContent={assistantContent}
              />
            ))}
          <MessageCitations citations={message.citations} />
          {assistantActions}
        </div>
      )}
    </div>
  );
}

export const MessageBubble = memo(forwardRef(MessageBubbleInner));
MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
